<script lang="ts">
    import type {UserAccountPageLoadedData, UserAccountReactivePageData, UserAccountStaticPageData} from './types';
    import type {DelayedReturnPenalty} from '../types';
    import {getInjector} from 'core/svelte-context/context';
    import {KpUserAccountPageService} from './kp-user-account-page.service';
    import {onDestroy, onMount} from 'svelte';
    import {isFunction} from 'shared/utils/custom-utils';
    import {createPageContext} from 'shared/layouts/page-context';
    import {UserRoles} from 'shared/services/current-auth.service';
    import KpLayoutBuilder from 'shared/layouts/grid-system/built-layout/KpLayoutBuilder.svelte';
    import KpUserAccountHeadingPart from './parts/KpUserAccountHeadingPart.svelte';
    import KpUserAccountInformationPart from './parts/KpUserAccountInformationPart.svelte';
    import KpUserAccountContactPart from './parts/KpUserAccountContactPart.svelte';
    import KpUserAccountAddressPart from './parts/KpUserAccountAddressPart.svelte';
    import KpUserAccountSearchHistoryAndExportPart from './parts/KpUserAccountSearchHistoryAndExportPart.svelte';
    import KpUserAccountLoansTabsetPart from './parts/KpUserAccountLoansTabsetPart.svelte';
    import KpUserAccountLibrarianMessagePart from './parts/KpUserAccountLibrarianMessagePart.svelte';
    import KpUserAccountLoanButtonsPart from './parts/KpUserAccountLoanButtonsPart.svelte';
    import KpUserAccountManagementButtonsPart from './parts/KpUserAccountManagementButtonsPart.svelte';
    import KpUserAccountFinancePanelPart from './parts/KpUserAccountFinancePanelPart.svelte';
    import KpUserAccountSdiRequestsPanelPart from './parts/KpUserAccountSdiRequestsPanelPart.svelte';
    import KpLayoutGridContainer from 'shared/layouts/grid-system/KpLayoutGridContainer.svelte';
    import SutorUserAccountTabbedSubpagesPart from './sutor-parts/SutorUserAccountTabbedSubpagesPart.svelte';

    export let pageData: UserAccountPageLoadedData;
    export let redirecting = false;

    const service = getInjector().getByToken<KpUserAccountPageService>(KpUserAccountPageService.serviceName);

    const userAccountComponents = {
        heading: KpUserAccountHeadingPart,
        accountInformation: KpUserAccountInformationPart,
        librarianMessage: KpUserAccountLibrarianMessagePart,
        userContacts: KpUserAccountContactPart,
        userAddresses: KpUserAccountAddressPart,
        finances: KpUserAccountFinancePanelPart,
        accountManagementButtons: KpUserAccountManagementButtonsPart,
        loanButtons: KpUserAccountLoanButtonsPart,
        loansTabset: KpUserAccountLoansTabsetPart,
        sdiRequests: KpUserAccountSdiRequestsPanelPart,
        searchHistoryAndExport: KpUserAccountSearchHistoryAndExportPart,

        // Sutor specific
        sutorTabbedSubpages: SutorUserAccountTabbedSubpagesPart
    };

    let delayedReturnPenaltyUnregister: () => void;

    const pageContext = createPageContext<UserAccountStaticPageData, UserAccountReactivePageData>(
        {user: pageData.user, detailView: pageData.detailView, userRecord: pageData.userRecord}, // Static
        {...pageData} // Reactive
    );

    onMount(() => {
        if (redirecting) {
            return;
        }

        delayedReturnPenaltyUnregister = service.addReloadDelayedReturnPenaltyHandler(handleReloadDelayedReturnPenalty);
        loadLoanLists();
    });

    onDestroy(() => {
        if (isFunction(delayedReturnPenaltyUnregister)) {
            delayedReturnPenaltyUnregister();
        }
    });

    function loadLoanLists() {
        pageData.loanList.loadTotalSize();
        pageData.loanList.loadFirstPage();

        pageData.waitingLoanList.loadTotalSize();
        pageData.endedLoanList.loadTotalSize();

        if (pageData.user.role.includes(UserRoles.ROLE_LIBRARY)) {
            pageData.providedSeekingsOfSeeker.loadTotalSize();
            pageData.seekingsOfProvider.loadTotalSize();
        } else {
            pageData.seekingsOfRequester.loadTotalSize();
        }
    }

    const handleReloadDelayedReturnPenalty = (event: CustomEvent<DelayedReturnPenalty>) => {
        pageData.delayedReturnPenalty = event.detail;
        pageContext.updateReactiveData({...pageData});
    };
</script>

{#if !redirecting}
    <KpLayoutGridContainer id="konto"
                           additionalClasses="kp-user-account-page"
                           rowsWidth="{pageData.detailView.viewLayout.settings.defaultRowWidth}">

        <KpLayoutBuilder layout="{pageData.detailView.viewLayout}" supportedComponents="{userAccountComponents}"/>
    </KpLayoutGridContainer>
{/if}